import { loc } from '../../lib/format';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import type { Paginated } from '@tcrd/shared';
import { Button, Card, cn, EmptyState, ErrorState, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { api, errorMessage } from '../../lib/api';
import { resolveNotificationUrl } from '../native/NativeBridge';

interface NotificationDto {
  id: string;
  type: string;
  title: string;
  body: string;
  data: { url?: string };
  readAt: string | null;
  createdAt: string;
}

export function useUnreadNotifications() {
  return useQuery({ queryKey: ['notifications', 'unread'], queryFn: () => api<{ count: number }>('/notifications/unread-count'), refetchInterval: 60_000 });
}

export function NotificationsPage({ back }: { back: string }) {
  const qc = useQueryClient();
  const history = useHistory();
  const q = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    initialPageParam: '',
    queryFn: ({ pageParam }) => api<Paginated<NotificationDto>>('/notifications', { query: { cursor: pageParam } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  const open = async (n: NotificationDto) => {
    if (!n.readAt) {
      await api(`/notifications/${n.id}/read`, { method: 'POST' }).catch(() => undefined);
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    }
    const target = resolveNotificationUrl(n.data?.url, back.startsWith('/driver') ? '/driver' : '/app');
    if (target) history.push(target);
  };

  const readAll = async () => {
    await api('/notifications/read-all', { method: 'POST' });
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  return (
    <Page
      title="Notificaciones"
      back={back}
      onRefresh={() => q.refetch()}
      actions={items.some((n) => !n.readAt) && <button className="tap px-2 flex items-center" onClick={readAll} aria-label="Marcar todas como leídas"><CheckCheck className="size-5" /></button>}
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {q.data && items.length === 0 && <EmptyState icon={<Bell className="size-6" />} title="Sin notificaciones" description="Aquí verás cotizaciones, cambios de viaje, pagos y avisos." />}
      <div className="flex flex-col gap-2">
        {items.map((n) => (
          <button key={n.id} onClick={() => open(n)} className="text-left">
            <Card className={cn('p-4 flex gap-3', !n.readAt && 'border-l-4 border-l-brand-red')}>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm', !n.readAt && 'font-semibold')}>{n.title}</p>
                <p className="text-sm text-[var(--text-muted)]">{n.body}</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">{new Date(n.createdAt).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' })}</p>
              </div>
            </Card>
          </button>
        ))}
        {q.hasNextPage && <Button variant="ghost" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>Ver más</Button>}
      </div>
    </Page>
  );
}
